import { HttpErrorResponse } from "@angular/common/http";
import { Component, Input, OnDestroy, signal, ViewChild } from "@angular/core";
import { ChartJsContainerComponent, rgb } from "@tylertech/chart-js";
import { ChartBindingObject, ChartDialogService, ChartManagerService, ChartRegistryService } from "@tylertech/charting-module";
import { Command, IconCommand } from "@tylertech/commands-module";
import { formatAbbreviatedNumber } from "@tylertech/common-components-module";
import { FileService, LoggingService } from "@tylertech/core-module";
import { ICustomViewComponent } from "@tylertech/custom-view";
import { IChartCriteria } from "@tylertech/tyler-api-types";
import { ChartConfiguration, TooltipItem } from "chart.js";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
    selector: "inventory-top-used-items-chart",
    templateUrl: "./topUsedItemsChart.tmpl.html",
    styleUrls: ["./topUsedItemsChart.component.scss"],
    standalone: false
})
export class TopUsedItemsChartComponent implements OnDestroy, ICustomViewComponent<ChartBindingObject>
{
    private readonly destructor$ = new Subject<void>();
    readonly commands: ReadonlyArray<Command>;
    chartConfig: ChartConfiguration<"bar">;
    private readonly _chartId: string;

    @ViewChild(ChartJsContainerComponent) private readonly chartContainer: ChartJsContainerComponent;
    readonly showChart = signal(false);

    private _chartCriteria: IChartCriteria;
    readonly customDataBinding: ChartBindingObject;
    isLoaded: boolean;
    readonly ChartTitle: string = "Top Used Items";

    constructor(
        $chartManagerService: ChartManagerService,
        private readonly $chartRegistryService: ChartRegistryService,
        private readonly $chartDialogService: ChartDialogService,
        private readonly $loggingService: LoggingService,
        private readonly $fileService: FileService
    )
    {
        $chartManagerService.refreshChartObservable.pipe(takeUntil(this.destructor$)).subscribe((chartId: string) =>
        {
            if (chartId !== this.chartId)
            {
                return;
            }
            this.loadChartData();
        });
        this.commands = [
            new IconCommand({
                icon: "share",
                label: null,
                action: () => this.shareChart(),
                getEnabled: () => this.hasData(),
                tooltip: `Share ${this.ChartTitle}`,
            }),
        ];
    }

    get chartId(): string | undefined
    {
        return this._chartId || this.customDataBinding?.chartId;
    }

    @Input()
    get chartCriteria(): IChartCriteria | undefined
    {
        return this._chartCriteria || this.customDataBinding?.chartCriteria;
    }

    set chartCriteria(value: IChartCriteria)
    {
        this._chartCriteria = value;
    }

    get chartHasData(): boolean
    {
        return this.hasData();
    }

    get errorMessage(): string | undefined
    {
        return this.chartCriteria?.ErrorMessage;
    }

    get showSettings(): boolean
    {
        return this.chartCriteria?.Parameters?.filter(g => g.Hidden === false)?.length > 0;
    }

    get ChartSubtitle(): string
    {
        const setting = this.chartCriteria?.Parameters?.find(v => v.Id === "TopUsedItemsChartType");
        const type = setting?.Options.find(t => t.Value === setting.Value)?.Display;
        return type;
    }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        new Promise((_resolveFn, rejectFn) =>
        {
            if (!this.hasData())
            {
                return this.loadChartData();
            }
            try
            {
                this.updateChartConfiguration();
            } catch (err)
            {
                rejectFn(err);
            }
        })
            .catch(e =>
            {
                console.error("Error on load", e);
            })
            .finally(() =>
            {
                this.isLoaded = true;
            });
    }

    hasData(): boolean
    {
        return (
            this.chartCriteria?.ChartDatas?.length > 0 &&
            this.chartCriteria?.ChartDatas[0]?.Data?.length > 0 &&
            this.chartCriteria?.ChartDatas[0].Data[0] &&
            this.chartCriteria.ChartDatas[0].Data[0].length > 0
        );
    }

    private loadChartData(): Promise<void>
    {
        this.isLoaded = false;
        return this.$chartRegistryService.GetChartData(this.chartCriteria)
            .then(
                (result: IChartCriteria) =>
                {
                    this.chartCriteria = result;
                    if (!this.hasData())
                    {
                        return;
                    }

                    this.updateChartConfiguration();
                },
                (error: HttpErrorResponse) =>
                {
                    this.$loggingService.error("Could not retrieve chart data", error);
                    this.chartCriteria = {
                        ErrorMessage: error.error.Message,
                    } as IChartCriteria;
                })
            .finally(() =>
            {
                this.isLoaded = true;
            });
    }

    openSettings(): void
    {
        this.$chartDialogService
            .showChartParametersDialog(this.ChartTitle, this.chartCriteria)
            .then((criteria: IChartCriteria) =>
            {
                this.chartCriteria = criteria;
                this.loadChartData();
            });
    }

    private shareChart(): void
    {
        const img = this.chartContainer.createImage();
        this.$fileService.openDocumentLink(img, "image/png", this.ChartTitle.replace(/ /g, ""));
    }

    private updateChartConfiguration(): void
    {
        this.chartConfig = {
            type: "bar",
            options: {
                indexAxis: "y",
                plugins: {
                    legend: { display: false },
                    tooltip: {
                        callbacks: {
                            title: (_tooltipItems: TooltipItem<"bar">[]) => this.ChartSubtitle,
                            label: (tooltipItem: TooltipItem<"bar">): string | string[] =>
                            {
                                return tooltipItem.label.toString();
                            },
                        },
                        backgroundColor: '#ffffff',
                        borderColor: '#767676',
                        borderWidth: 1
                    },
                },
                maintainAspectRatio: false,
                scales: {
                    x: {
                        type: "linear",
                        display: true,
                        grid: { display: true },
                        ticks: {
                            callback: (value: number) =>
                            {
                                return formatAbbreviatedNumber(value.toFixed(value > -10 && value < 10 ? 2 : 0));
                            },
                        },
                    },
                    y: {
                        display: true,
                        grid: {
                            display: false,
                        },
                    },
                },
            },
            data: {
                datasets: [
                    {
                        data: this.chartCriteria.ChartDatas[0].Data[0],
                        backgroundColor: rgb(0, 143, 140, 1),
                        borderColor: rgb(0, 143, 140, 1),
                        hoverBackgroundColor: rgb(0, 143, 140, 1),
                        hoverBorderColor: rgb(0, 143, 140, 1),
                        barPercentage: 0.8,
                    },
                ],
                labels: this.chartCriteria.ChartDatas[0].Labels
                    .map(x => x.length >= 20 ? `${x.substring(0, 17)}...` : x),
            },
        };

        this.showChart.set(this.chartCriteria?.ChartDatas?.length > 0);
    }
}
